import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import theme from "theme";
import { footer } from "content/site";
import { newTabLinkProps } from "utils/newTabLinkProps";
import { Container } from "components/marketing/primitives";

const Foot = styled.footer`
	background: ${theme.color.dark};
	color: ${theme.color.lightD1};
	padding: 3rem 0 2rem;
	margin-top: auto;
	border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
	gap: 2rem 2.5rem;
	margin-bottom: 2.5rem;
`;

const ColTitle = styled.div`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: ${theme.color.grey};
	margin-bottom: 0.75rem;
`;

const ColList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
`;

const ColItem = styled.li`
	margin-bottom: 0.5rem;
`;

const FooterExternal = styled.a`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.lightD1};
	text-decoration: none;
	&:hover {
		color: #fff;
		text-decoration: underline;
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
		border-radius: 2px;
	}
`;

const footerNavLink = `
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.lightD1};
	text-decoration: none;
	&:hover {
		color: #fff;
		text-decoration: underline;
	}
	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
		border-radius: 2px;
	}
`;

const FooterHashLink = styled(HashLink)`
	${footerNavLink}
`;

const FooterRouterLink = styled(Link)`
	${footerNavLink}
`;

const FooterMuted = styled.span`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.greyD1};
`;

const Bottom = styled.div`
	padding-top: 1.5rem;
	border-top: 1px solid ${theme.color.darkL2};
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	color: ${theme.color.grey};
`;

const Tagline = styled.p`
	margin: 0 0 0.5rem;
	color: ${theme.color.greyD1};
	font-size: 0.875rem;
`;

function renderFooterLink(link) {
	if ("href" in link && link.href) {
		return (
			<FooterExternal href={link.href} {...newTabLinkProps(link.href)}>
				{link.label}
			</FooterExternal>
		);
	}
	if ("href" in link && link.optional && !link.href) {
		return <FooterMuted>{link.label} — coming soon</FooterMuted>;
	}
	if ("href" in link && !link.href) {
		return <FooterMuted>{link.label}</FooterMuted>;
	}
	if ("to" in link && link.to.startsWith("#")) {
		return (
			<FooterHashLink smooth to={link.to}>
				{link.label}
			</FooterHashLink>
		);
	}
	return (
		<FooterRouterLink to={link.to} {...newTabLinkProps(link.to)}>
			{link.label}
		</FooterRouterLink>
	);
}

export default function SiteFooter() {
	return (
		<Foot>
			<Container>
				<Grid>
					{footer.columns.map((col) => (
						<div key={col.title}>
							<ColTitle>{col.title}</ColTitle>
							<ColList>
								{col.links.map((link) => (
									<ColItem key={link.label}>{renderFooterLink(link)}</ColItem>
								))}
							</ColList>
						</div>
					))}
				</Grid>
				<Tagline>{footer.tagline}</Tagline>
				<Bottom>{footer.copyright}</Bottom>
			</Container>
		</Foot>
	);
}
